import { queryClient } from '@/api/queryClient';
import { useAuthStore } from '@/stores/auth';

export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
export const PERMISSIONS_STALE_EVENT = 'ted:permissions-stale';

/**
 * Fully terminate the session: clear auth state AND drop all cached private data
 * so a stale session's data can't linger in the QueryClient after a 401/logout.
 */
export function clearSession(): void {
  useAuthStore.getState().clear();
  queryClient.removeQueries();
}

/** Error carrying the HTTP status and the backend's `detail` message. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshInFlight: { generation: number; promise: Promise<boolean> } | null = null;

export function sessionGeneration(): number {
  return useAuthStore.getState().generation;
}

export function assertCurrentSession(generation: number): void {
  if (generation !== sessionGeneration()) throw new DOMException('Session changed', 'AbortError');
}

let cookieOperationQueue: Promise<void> = Promise.resolve();

function tokenSubject(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))) as { sub?: unknown };
    return typeof claims.sub === 'string' ? claims.sub : null;
  } catch {
    return null;
  }
}

/** Order all responses that can change the shared refresh cookie. */
function withSessionCookieLock<T>(operation: () => Promise<T>): Promise<T> {
  const pending = cookieOperationQueue.then(() => {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request('ted:session-cookie', operation);
    }
    return operation();
  });
  cookieOperationQueue = pending.then(
    () => undefined,
    () => undefined,
  );
  return pending;
}

/**
 * Exchange the refresh cookie for a new access token. Returns success.
 *
 * Concurrent callers share ONE in-flight request: a burst of 401s (e.g. a
 * dashboard firing many queries at once) must rotate the single-use refresh
 * cookie exactly once. Parallel refreshes would race the rotation — only the
 * first would succeed and the rest would 401 and spuriously log the user out.
 */
export async function tryRefresh(): Promise<boolean> {
  const generation = sessionGeneration();
  if (refreshInFlight?.generation === generation) return refreshInFlight.promise;
  const promise = withSessionCookieLock(async () => {
    try {
      if (generation !== sessionGeneration()) return false;
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access_token: string };
      if (generation !== sessionGeneration()) return false;
      const currentUser = useAuthStore.getState().user;
      const subject = tokenSubject(data.access_token);
      if (currentUser && subject && subject !== currentUser.id) {
        clearSession();
        return false;
      }
      useAuthStore.getState().setAccessToken(data.access_token);
      return true;
    } catch {
      return false;
    }
  });
  const operation = { generation, promise };
  refreshInFlight = operation;
  try {
    return await promise;
  } finally {
    if (refreshInFlight === operation) refreshInFlight = null;
  }
}

/**
 * Typed fetch wrapper. Attaches the bearer token, sends the refresh cookie,
 * and on a 401 transparently refreshes the token and retries once.
 */
export async function apiFetchResponse(
  path: string,
  options: RequestInit = {},
  retryOnUnauthorized = true,
  generation = sessionGeneration(),
): Promise<Response> {
  assertCurrentSession(generation);
  const token = useAuthStore.getState().accessToken;
  const headers = new Headers(options.headers);
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const send = () =>
    fetch(`${API_URL}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  const response = await (path === '/auth/login' || path === '/auth/logout'
    ? withSessionCookieLock(() => {
        // An older logout must still revoke its cookie before the queued login.
        if (path !== '/auth/logout') assertCurrentSession(generation);
        return send();
      })
    : send());
  assertCurrentSession(generation);

  if (response.status === 401 && retryOnUnauthorized) {
    if (useAuthStore.getState().accessToken !== token || (await tryRefresh())) {
      assertCurrentSession(generation);
      return apiFetchResponse(path, options, false, generation);
    }
    assertCurrentSession(generation);
    clearSession();
    throw new ApiError(401, 'Unauthorized');
  }

  if (!response.ok) {
    if (response.status === 403 && path !== '/auth/me' && token && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PERMISSIONS_STALE_EVENT));
    }
    let message = response.statusText;
    let detail: unknown;
    try {
      const body = (await response.json()) as { detail?: unknown };
      detail = body.detail;
      if (typeof detail === 'string') message = detail;
    } catch {
      // Non-JSON error body - keep the status text.
    }
    assertCurrentSession(generation);
    throw new ApiError(response.status, message, detail);
  }

  return response;
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retryOnUnauthorized = true,
): Promise<T> {
  const generation = sessionGeneration();
  const response = await apiFetchResponse(path, options, retryOnUnauthorized, generation);
  if (response.status === 204) return undefined as T;
  const data = (await response.json()) as T;
  assertCurrentSession(generation);
  return data;
}
